<template>
    <USlideover
        v-if="milo.everOpened"
        v-model:open="milo.open"
        side="right"
        :modal="false"
        :overlay="false"
        :dismissible="false"
        :close="false"
        :title="$t('milo.title')"
        :description="intro"
        :ui="{ content: 'max-w-full sm:max-w-[440px]' }"
    >
        <template #content>
            <div class="flex h-full flex-col">
                <!-- Milo himself, the name and what is left today, then the thread's two actions. -->
                <div class="flex items-center gap-3 border-b border-default px-3 py-2">
                    <MiloFigure :key="figure3d ? '3d' : 'still'" :state="milo.avatar" framing="head" class="size-20 shrink-0" :enable3d="figure3d" />

                    <div class="min-w-0 flex-1">
                        <p class="truncate text-sm font-semibold text-highlighted">{{ $t('milo.title') }}</p>
                        <p class="truncate text-xs text-muted">{{ subtitle }}</p>
                    </div>

                    <UButton
                        icon="i-lucide-message-square-plus"
                        color="neutral"
                        variant="ghost"
                        size="sm"
                        :aria-label="$t('milo.new_conversation')"
                        :title="$t('milo.new_conversation')"
                        :disabled="!available || (!milo.messages.length && !milo.failure)"
                        @click="milo.startNew()"
                    />
                    <UButton
                        icon="i-lucide-x"
                        color="neutral"
                        variant="ghost"
                        size="sm"
                        :aria-label="$t('general.close')"
                        :title="`${$t('general.close')} (Esc)`"
                        @click="milo.hide()"
                    />
                </div>

                <!-- Not configured: the provider admin is told where the key goes. -->
                <div v-if="!available" class="flex flex-1 flex-col gap-3 p-4">
                    <UAlert
                        color="warning"
                        variant="subtle"
                        icon="i-lucide-key-round"
                        :title="$t('milo.not_configured_title')"
                        :description="$t('milo.not_configured_help')"
                    />
                    <UButton
                        v-if="appStore.isProviderAdmin()"
                        :to="{ name: 'milo-provider' }"
                        icon="i-lucide-settings-2"
                        :label="$t('milo.open_provider_settings')"
                        color="neutral"
                        variant="outline"
                        class="self-start"
                        @click="milo.hide()"
                    />
                </div>

                <template v-else>
                    <div ref="scroller" class="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3">
                        <!-- The greeting stands first in an empty thread. -->
                        <div v-if="!milo.messages.length && milo.status?.greeting" class="max-w-[85%] self-start rounded-lg bg-elevated px-3 py-2 text-sm text-default">
                            {{ milo.status.greeting }}
                        </div>

                        <template v-for="message in milo.messages" :key="message.id">
                            <div
                                v-if="message.role === 'user'"
                                class="max-w-[85%] self-end whitespace-pre-line rounded-lg bg-primary/10 px-3 py-2 text-sm text-highlighted"
                            >{{ message.text }}</div>

                            <div v-else class="flex max-w-[92%] flex-col gap-2 self-start">
                                <p v-if="message.error" class="flex items-center gap-1.5 rounded-lg bg-error/10 px-3 py-2 text-sm text-error">
                                    <UIcon name="i-lucide-circle-alert" class="size-4 shrink-0" />
                                    {{ miloErrorText(message.error.code) }}
                                </p>
                                <p v-else-if="message.text" class="whitespace-pre-line rounded-lg bg-elevated px-3 py-2 text-sm text-default">{{ message.text }}</p>

                                <MiloProposalCard
                                    v-for="proposal in message.proposals ?? []"
                                    :key="proposal.id"
                                    :proposal="proposal"
                                    :busy="milo.deciding[proposal.id]"
                                    :now="now"
                                    :changing="milo.picturing[proposal.id]"
                                    @decide="(item, confirm) => milo.decide(item, confirm)"
                                    @picture="(item, action) => milo.picture(item, action)"
                                />
                            </div>
                        </template>

                        <!-- The reply being written: what Milo looks up, then the words as they come. -->
                        <div v-if="milo.live" class="flex max-w-[92%] flex-col gap-2 self-start">
                            <p v-if="!milo.live.text" class="flex items-center gap-2 rounded-lg bg-elevated px-3 py-2 text-sm text-muted">
                                <UIcon name="i-lucide-loader-circle" class="size-4 animate-spin" />
                                {{ milo.live.activity ? toolLabel(milo.live.activity) : $t('milo.thinking') }}
                            </p>
                            <p v-else class="whitespace-pre-line rounded-lg bg-elevated px-3 py-2 text-sm text-default">{{ milo.live.text }}<span class="ml-0.5 inline-block h-3.5 w-1.5 animate-pulse bg-primary align-middle" /></p>

                            <MiloProposalCard
                                v-for="proposal in milo.live.proposals"
                                :key="proposal.id"
                                :proposal="proposal"
                                :now="now"
                                :busy="true"
                            />
                        </div>

                        <UAlert
                            v-if="milo.failure"
                            color="error"
                            variant="subtle"
                            icon="i-lucide-circle-alert"
                            :title="milo.failure.text"
                            :actions="milo.failure.code === MiloError.ConversationFull ? [{ label: $t('milo.new_conversation'), color: 'neutral', variant: 'outline', onClick: () => { void milo.startNew() } }] : []"
                        />
                    </div>

                    <div class="flex flex-col gap-2 border-t border-default p-3">
                        <div v-if="milo.suggestions.length && !milo.sending" class="flex flex-wrap gap-1.5">
                            <UButton
                                v-for="chip in milo.suggestions"
                                :key="chip.id"
                                :label="chip.text"
                                :icon="chip.kind === 'action' ? 'i-lucide-wand-sparkles' : undefined"
                                color="neutral"
                                variant="soft"
                                size="xs"
                                class="max-w-full"
                                :ui="{ label: 'truncate' }"
                                @click="milo.send(chip.text, chip.id)"
                            />
                        </div>

                        <UForm :state="form" :schema="schema" :validate-on="[]" class="flex items-end gap-2" @submit="submit">
                            <UFormField name="text" class="min-w-0 flex-1">
                                <UTextarea
                                    v-model="form.text"
                                    :rows="1"
                                    :maxrows="6"
                                    autoresize
                                    :maxlength="maxChars"
                                    :placeholder="$t('milo.placeholder')"
                                    :disabled="milo.sending"
                                    class="w-full"
                                    @keydown.enter.exact.prevent="submit"
                                />
                            </UFormField>
                            <UButton type="submit" icon="i-lucide-send-horizontal" :aria-label="$t('milo.send')" :loading="milo.sending" :disabled="!form.text.trim()" />
                        </UForm>
                    </div>
                </template>
            </div>
        </template>
    </USlideover>
</template>

<script lang="ts" setup>
    import { useEventListener, useIntervalFn } from '@vueuse/core'
    import { computed, nextTick, ref, useTemplateRef, watch } from 'vue'
    import { useI18n } from 'vue-i18n'
    import * as z from 'zod'

    import MiloFigure from '@/components/MiloFigure.vue'
    import MiloProposalCard from '@/components/milo/MiloProposalCard.vue'
    import { TenantRole } from '@/enums/tenant'
    import { MiloError, miloErrorText } from '@/libs/miloChat'
    import { useAppStore } from '@/stores/app'
    import { useMiloStore } from '@/stores/milo'
    import { useTenantStore } from '@/stores/tenant'

    /*
     * Milo beside the page, not instead of it (as Imprion PMS's assistant drawer): non-modal, no
     * overlay, not dismissed by a click outside — the person asks about the channel list in
     * front of them and presses Potrdi with it still there. Escape and the X close it; the thread
     * lives in `stores/milo.ts`, so closing keeps it. Not rendered before the first open.
     */
    const { t }       = useI18n()
    const appStore    = useAppStore()
    const tenantStore = useTenantStore()
    const milo        = useMiloStore()

    /** What Milo is for: the kitchen's and housekeeping's own requests, else the console (§14.1). */
    const intro = computed((): string => {
        switch (appStore.closedRole(milo.tid)) {
            case TenantRole.Kitchen:      return t('milo.intro_kitchen')
            case TenantRole.Housekeeping: return t('milo.intro_housekeeping')
            default:                      return t('milo.intro')
        }
    })

    // Escape closes the drawer wherever the focus is (it is non-modal: the page keeps it), unless
    // something above it — a dialog — took the key first.
    useEventListener(document, 'keydown', (event: KeyboardEvent) => {
        if (event.key === 'Escape' && milo.open && !event.defaultPrevented) milo.hide()
    })

    const scroller = useTemplateRef<HTMLElement>('scroller')
    const form     = ref({ text: '' })
    const now      = ref(Date.now())

    // A pending card turns "expired" on its own after five minutes.
    useIntervalFn(() => { now.value = Date.now() }, 15_000)

    const available = computed((): boolean => !!milo.status?.available)

    /** The house's figure_3d (§14.2): the moving head only once the status says so; keyed remount. */
    const figure3d = computed((): boolean => Boolean(milo.status) && milo.status?.figure_3d !== false)

    const maxChars = computed((): number => milo.status?.limits?.max_chars ?? 2000)

    const schema = computed(() => z.object({
        text: z.string().trim().min(1, t('validation.required')).max(maxChars.value, t('validation.too_long')),
    }))

    const subtitle = computed((): string => {
        const tenant = tenantStore.tenants.find((item) => item.id === milo.tid)?.name ?? tenantStore.tenant?.name ?? ''
        const left   = milo.leftToday

        return left === null ? tenant : [tenant, t('milo.left_today', left)].filter(Boolean).join(' · ')
    })

    const TOOLS = ['overview', 'list_devices', 'list_rooms', 'list_channels', 'home_tiles', 'list_invitations', 'screen_themes', 'find_local_events', 'propose_local_event', 'transit_departures', 'travel_time']

    const toolLabel = (tool: string): string => TOOLS.includes(tool) ? t(`milo.tool.${tool}`) : t('milo.looking_up')

    const submit = () => {
        const text = form.value.text.trim()

        if (!text || milo.sending || text.length > maxChars.value) return

        form.value.text = ''
        void milo.send(text)
    }

    const toBottom = async () => {
        await nextTick()
        scroller.value?.scrollTo({ top: scroller.value.scrollHeight })
    }

    watch(() => [milo.messages.length, milo.live?.text, milo.live?.proposals.length, milo.failure, milo.open], () => { void toBottom() })
</script>
